/**
 * KitchenDisplay - Kitchen Display System (KDS) for kitchen staff
 * Real-time order monitoring with timers, status updates, and sound alerts
 */
import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ChefHat, Clock, Check, AlertTriangle, Bell, 
  Flame, Timer, Package, RefreshCw, Volume2, VolumeX,
  User, Maximize2, X
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KitchenDisplay = () => {
  const queryClient = useQueryClient();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [previousPendingCount, setPreviousPendingCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const audioRef = useRef(null);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Update time every second for timers
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Initialize audio
  useEffect(() => {
    // Create audio context for notification sounds
    audioRef.current = new Audio('data:audio/wav;base64,UklGRlQGAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YTAGAACAf4B/gH+Af4B/gH9/gICBf4KAgYCBgIGAgYCBgYGCgYKBg4GDgYOCg4KDg4SDhISEhIWEhYWFhoWGhoeGh4eHh4iIiIiIiImJiYmJioqKioqKi4uLi4yMjIyMjY2NjY6Ojo6Ojo+Pj4+PkJCQkJGRkZGRkpKSkpOTk5OUlJSUlJWVlZaWlpaXl5eXl5iYmJmZmZmampqam5ubm5ycnJycnZ2dnp6enp+fn5+goKCgoKGhoaKioqKjo6OkpKSkpKWlpaampqanp6eoqKioqampr6+vsLGxsbK0tLS1tra3uLi5uru7vL29vr+/wMHBwsPDxMXFxsfHyMnJysvLzM3Nzs/P0NHS0tPU1NXW1tfY2Nna2tvb3N3d3t/f4OHh4uPj5OXl5ufn6Onp6uvr7O3t7u/v8PHx8vPz9PX19vf3+Pn5+vv7/P39/v//');
    audioRef.current.volume = 0.5;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Play notification sound for new orders
  const playNotificationSound = () => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Autoplay might be blocked, that's OK
      });
    }
  };

  // Fetch active orders (pending, preparing, ready)
  const { data: orders = [], isLoading, refetch } = useQuery({
    queryKey: ['kitchen-orders'],
    queryFn: async () => {
      const [pending, preparing, ready] = await Promise.all([
        fetch(`${API_URL}/api/admin/dining/orders?status=pending&today_only=true`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/dining/orders?status=preparing&today_only=true`, { headers }).then(r => r.json()),
        fetch(`${API_URL}/api/admin/dining/orders?status=ready&today_only=true`, { headers }).then(r => r.json())
      ]);
      return [...pending, ...preparing, ...ready];
    },
    refetchInterval: 3000 // Refresh every 3 seconds for real-time feel
  });

  // Check for new pending orders and play sound
  useEffect(() => {
    const pendingCount = orders.filter(o => o.status === 'pending').length;
    if (pendingCount > previousPendingCount && previousPendingCount > 0) {
      playNotificationSound();
      toast.info('🔔 New order received!', { duration: 3000 });
    }
    setPreviousPendingCount(pendingCount);
  }, [orders, previousPendingCount, soundEnabled]);

  // Update order status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ orderId, status }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/orders/${orderId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Failed to update status');
      return res.json();
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries(['kitchen-orders']);
      queryClient.invalidateQueries(['dining-orders']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success(`Order marked as ${status}!`);
    },
    onError: (error) => toast.error(error.message)
  });

  // Calculate wait time and urgency
  const getWaitInfo = (createdAt, preparingAt) => {
    const created = new Date(createdAt);
    const diffMs = currentTime - created;
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    let urgency = 'normal';
    if (diffMins >= 15) urgency = 'critical';
    else if (diffMins >= 10) urgency = 'warning';
    else if (diffMins >= 5) urgency = 'attention';
    
    // Also calculate cooking time if preparing
    let cookingTime = null;
    if (preparingAt) {
      const prepStart = new Date(preparingAt);
      const cookMs = currentTime - prepStart;
      const cookMins = Math.floor(cookMs / 60000);
      const cookSecs = Math.floor((cookMs % 60000) / 1000);
      cookingTime = `${cookMins}:${cookSecs.toString().padStart(2, '0')}`;
    }
    
    return {
      minutes: diffMins,
      seconds: diffSecs,
      formatted: `${diffMins}:${diffSecs.toString().padStart(2, '0')}`,
      urgency,
      cookingTime
    };
  };

  const urgencyColors = {
    normal: 'bg-green-500',
    attention: 'bg-yellow-500',
    warning: 'bg-orange-500',
    critical: 'bg-red-500 animate-pulse'
  };

  const urgencyBgColors = {
    normal: 'border-green-200',
    attention: 'border-yellow-200 bg-yellow-50/50',
    warning: 'border-orange-200 bg-orange-50/50',
    critical: 'border-red-300 bg-red-50/50'
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const pendingOrders = orders.filter(o => o.status === 'pending');
  const preparingOrders = orders.filter(o => o.status === 'preparing');
  const readyOrders = orders.filter(o => o.status === 'ready');

  const OrderCard = ({ order, showActions = true }) => {
    const waitInfo = getWaitInfo(order.created_at, order.preparing_at);
    
    return (
      <Card 
        className={`relative overflow-hidden transition-all ${
          order.status === 'ready' 
            ? 'bg-green-50 border-green-300 shadow-green-100' 
            : urgencyBgColors[waitInfo.urgency]
        }`}
        data-testid={`order-card-${order.order_number}`}
      >
        {/* Urgency indicator bar */}
        <div className={`absolute top-0 left-0 right-0 h-1.5 ${urgencyColors[waitInfo.urgency]}`} />
        
        <CardContent className="p-4 pt-5">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-2xl font-bold tracking-tight">{order.order_number}</h3>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="capitalize font-medium">{order.order_type?.replace('_', ' ')}</span>
                {order.order_type === 'dine_in' && (
                  <Badge variant="outline" className="font-semibold">Table</Badge>
                )}
                {order.payment_method === 'included' && (
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">Included</Badge>
                )}
              </div>
              {/* Customer info */}
              {order.customer_name && (
                <div className="flex items-center gap-1 mt-1 text-sm text-gray-600">
                  <User className="h-3 w-3" />
                  <span>{order.customer_name}</span>
                </div>
              )}
            </div>
            <div className={`text-right ${waitInfo.urgency === 'critical' ? 'text-red-600' : ''}`}>
              <div className="flex items-center gap-1 justify-end">
                <Timer className="h-4 w-4" />
                <span className="text-3xl font-mono font-bold">{waitInfo.formatted}</span>
              </div>
              {order.status === 'preparing' && waitInfo.cookingTime && (
                <div className="text-sm text-orange-600 flex items-center gap-1 justify-end">
                  <Flame className="h-3 w-3" />
                  <span>Cooking: {waitInfo.cookingTime}</span>
                </div>
              )}
              <span className="text-xs text-gray-500">
                {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          </div>

          {/* Items */}
          <div className="space-y-2 mb-4">
            {order.items?.map((item, idx) => (
              <div 
                key={idx} 
                className="flex items-start justify-between p-2 bg-white rounded border shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <span className="text-2xl font-bold text-blue-600 w-10 text-center">{item.quantity}x</span>
                  <div>
                    <span className="font-semibold text-gray-900">{item.name}</span>
                    {item.notes && (
                      <p className="text-sm text-orange-600 mt-0.5">⚠️ {item.notes}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Order notes */}
          {order.notes && (
            <div className="p-2 bg-yellow-100 rounded border border-yellow-300 mb-4">
              <p className="text-sm font-medium"><strong>📝 Note:</strong> {order.notes}</p>
            </div>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex gap-2">
              {order.status === 'pending' && (
                <Button
                  className="flex-1 h-14 text-lg bg-orange-500 hover:bg-orange-600"
                  onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'preparing' })}
                  disabled={updateStatusMutation.isPending}
                  data-testid={`start-cooking-${order.order_number}`}
                >
                  <Flame className="h-6 w-6 mr-2" />
                  START COOKING
                </Button>
              )}
              {order.status === 'preparing' && (
                <Button
                  className="flex-1 h-14 text-lg bg-green-500 hover:bg-green-600"
                  onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'ready' })}
                  disabled={updateStatusMutation.isPending}
                  data-testid={`mark-ready-${order.order_number}`}
                >
                  <Check className="h-6 w-6 mr-2" />
                  READY FOR PICKUP
                </Button>
              )}
              {order.status === 'ready' && (
                <Button
                  className="flex-1 h-14 text-lg"
                  variant="outline"
                  onClick={() => updateStatusMutation.mutate({ orderId: order.id, status: 'served' })}
                  disabled={updateStatusMutation.isPending}
                  data-testid={`mark-picked-${order.order_number}`}
                >
                  <Package className="h-6 w-6 mr-2" />
                  PICKED UP
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-white p-4" data-testid="kitchen-display">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <ChefHat className="h-10 w-10 text-orange-500" />
          <div>
            <h1 className="text-2xl font-bold">Kitchen Display System</h1>
            <p className="text-slate-400">
              {currentTime.toLocaleTimeString()} • {orders.length} active orders
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Stats */}
          <div className="flex gap-4 text-center">
            <div className="px-4 py-2 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
              <p className="text-3xl font-bold text-yellow-400">{pendingOrders.length}</p>
              <p className="text-xs text-yellow-300 font-medium">NEW</p>
            </div>
            <div className="px-4 py-2 bg-orange-500/20 rounded-lg border border-orange-500/30">
              <p className="text-3xl font-bold text-orange-400">{preparingOrders.length}</p>
              <p className="text-xs text-orange-300 font-medium">COOKING</p>
            </div>
            <div className="px-4 py-2 bg-green-500/20 rounded-lg border border-green-500/30">
              <p className="text-3xl font-bold text-green-400">{readyOrders.length}</p>
              <p className="text-xs text-green-300 font-medium">READY</p>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`text-white border-slate-600 ${soundEnabled ? 'bg-slate-700' : ''}`}
              title={soundEnabled ? 'Mute alerts' : 'Enable alerts'}
            >
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            <Button 
              variant="outline" 
              size="icon"
              onClick={toggleFullscreen}
              className="text-white border-slate-600"
              title="Toggle fullscreen"
            >
              {isFullscreen ? <X className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => refetch()}
              className="text-white border-slate-600"
            >
              <RefreshCw className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Urgency Legend */}
      <div className="flex gap-6 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span className="text-slate-400">&lt;5 min</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-yellow-500" />
          <span className="text-slate-400">5-10 min</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-orange-500" />
          <span className="text-slate-400">10-15 min</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500 animate-pulse" />
          <span className="text-slate-400">&gt;15 min (URGENT)</span>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-slate-400">Loading orders...</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20">
          <ChefHat className="h-24 w-24 mx-auto mb-4 text-slate-600" />
          <h2 className="text-3xl font-bold text-slate-500">All Caught Up!</h2>
          <p className="text-slate-600 text-lg mt-2">No active orders right now</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {/* New Orders Column */}
          {pendingOrders.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-yellow-400 bg-yellow-500/10 px-3 py-2 rounded-lg">
                <Bell className="h-5 w-5 animate-bounce" />
                <h2 className="font-bold text-lg">NEW ORDERS ({pendingOrders.length})</h2>
              </div>
              {pendingOrders
                .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
                .map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
            </div>
          )}

          {/* Preparing Orders */}
          {preparingOrders.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-orange-400 bg-orange-500/10 px-3 py-2 rounded-lg">
                <Flame className="h-5 w-5" />
                <h2 className="font-bold text-lg">COOKING ({preparingOrders.length})</h2>
              </div>
              {preparingOrders
                .sort((a, b) => new Date(a.preparing_at || a.created_at) - new Date(b.preparing_at || b.created_at))
                .map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
            </div>
          )}

          {/* Ready Orders */}
          {readyOrders.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-green-400 bg-green-500/10 px-3 py-2 rounded-lg">
                <Check className="h-5 w-5" />
                <h2 className="font-bold text-lg">READY FOR PICKUP ({readyOrders.length})</h2>
              </div>
              {readyOrders
                .sort((a, b) => new Date(a.ready_at || a.created_at) - new Date(b.ready_at || b.created_at))
                .map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
            </div>
          )}
        </div>
      )}

      {/* Floating alert for new orders */}
      {pendingOrders.length > 0 && (
        <div className="fixed bottom-6 right-6">
          <div className="bg-yellow-500 text-black px-6 py-3 rounded-xl shadow-2xl animate-bounce flex items-center gap-2">
            <Bell className="h-6 w-6" />
            <span className="font-bold text-lg">
              {pendingOrders.length} new order{pendingOrders.length > 1 ? 's' : ''}!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default KitchenDisplay;
